import React, { useRef, useState } from "react";
import { API, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic Excel/CSV import button. Calls:
 *   POST /api/import/{kind}/preview  → returns { total_rows, ... }
 *   POST /api/import/{kind}          → returns { inserted, updated|skipped_duplicate, ... }
 */
export default function ImportButton({ kind, label, accept = ".xlsx,.xls", onDone, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const reset = () => { setFile(null); setPreview(null); setResult(null); if (ref.current) ref.current.value = ""; };

  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const token = localStorage.getItem("access_token");
      const r = await fetch(`${API}/import/${kind}/preview`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiErrorDetail(data.detail) || `HTTP ${r.status}`);
      setPreview(data);
    } catch (e) { toast.error("Preview failed", { description: e.message }); }
    finally { setLoading(false); }
  };

  const doImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const token = localStorage.getItem("access_token");
      const r = await fetch(`${API}/import/${kind}`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiErrorDetail(data.detail) || `HTTP ${r.status}`);
      setResult(data);
      toast.success("Import complete");
      onDone && onDone();
    } catch (e) { toast.error("Import failed", { description: e.message }); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid={`${testIdPrefix}-btn`}>
          <Upload size={14} className="mr-1" /> {label || `Import ${kind}`}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{label || `Import ${kind}`}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <input
            ref={ref} type="file" accept={accept}
            onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setResult(null); }}
            className="block w-full text-sm text-slate-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-200 file:text-xs file:font-semibold file:bg-slate-50 hover:file:bg-slate-100"
            data-testid={`${testIdPrefix}-file`}
          />
          {file && !preview && (
            <Button onClick={doPreview} disabled={loading} size="sm" className="bg-slate-900 text-white" data-testid={`${testIdPrefix}-preview-btn`}>
              {loading ? "Reading..." : "Preview"}
            </Button>
          )}
          {preview && (
            <div className="bg-slate-50 border rounded-md p-3 text-sm">
              <div className="font-semibold mb-1">{preview.total_rows} rows detected</div>
              {preview.by_brand && (
                <div className="text-xs text-slate-600 mb-2">
                  {Object.entries(preview.by_brand).map(([b, n]) => <span key={b} className="inline-block mr-3"><span className="font-mono font-semibold">{n}</span> {b}</span>)}
                </div>
              )}
              {preview.preview && (
                <div className="text-xs text-slate-600 max-h-32 overflow-y-auto font-mono">
                  {(Array.isArray(preview.preview) ? preview.preview : []).slice(0, 10).map((p, i) => (
                    <div key={i} className="truncate">{typeof p === "string" ? p : JSON.stringify(p)}</div>
                  ))}
                </div>
              )}
            </div>
          )}
          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm flex items-start gap-2" data-testid={`${testIdPrefix}-result`}>
              <CheckCircle2 size={16} className="text-emerald-600 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-900">Import complete</div>
                <div className="text-xs text-emerald-800">
                  {result.inserted != null && <span>Inserted: <b>{result.inserted}</b> · </span>}
                  {result.updated != null && <span>Updated: <b>{result.updated}</b> · </span>}
                  {result.skipped_duplicate != null && <span>Skipped (dup): <b>{result.skipped_duplicate}</b> · </span>}
                  {result.skipped != null && <span>Skipped: <b>{result.skipped}</b> · </span>}
                  Total rows: {result.total_rows}
                </div>
                {result.errors?.length > 0 && (
                  <div className="text-xs text-rose-700 mt-1">{result.errors.length} errors (first: {JSON.stringify(result.errors[0])})</div>
                )}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          {preview && !result && <Button onClick={doImport} disabled={loading} className="bg-slate-900 text-white" data-testid={`${testIdPrefix}-confirm`}>{loading ? "Importing..." : `Import ${preview.total_rows} rows`}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
